// Copied from frontend/src/lib/lazyWithRetry.ts — auto-reload once when a
// redeploy invalidates content-hashed lazy chunks.
import { lazy, type ComponentType } from "react";

const RELOAD_KEY = "icc-chunk-reload-ts";
const RELOAD_WINDOW_MS = 10_000;

export function chunkReloadOnce(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last > RELOAD_WINDOW_MS) {
      window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
      window.location.reload();
      return true;
    }
  } catch {
    // sessionStorage unavailable (private mode) — no reload loop risk
  }
  return false;
}

export function isChunkLoadError(err: unknown): boolean {
  const msg = (err as { message?: string })?.message || String(err);
  return (
    /dynamically imported module/i.test(msg) ||
    /Importing a module script failed/i.test(msg) ||
    /ChunkLoadError/i.test(msg) ||
    /Failed to fetch/i.test(msg)
  );
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithRetry<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory();
      try {
        window.sessionStorage.removeItem(RELOAD_KEY);
      } catch {
        /* ignore */
      }
      return mod;
    } catch (err) {
      if (isChunkLoadError(err) && chunkReloadOnce()) {
        return new Promise<{ default: T }>(() => {});
      }
      throw err;
    }
  });
}
